import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { TableHead, Table, TableCell, TableRow, TableBody, Paper } from '@material-ui/core';
import TableContainer from '@material-ui/core/Table';
import { isRealNumber } from '../../common/utils';

// Guard the contribution columns: when a domain-weight object is present but its
// weight/weightedScore is missing, show '' instead of crashing (undefined.toFixed)
// or printing 'NaN%'.
const weightPct = (w) => (w && isRealNumber(w.weight) ? `${(w.weight * 100).toFixed(2)}%` : '');
const weightScore = (w) => (w && isRealNumber(w.weightedScore) ? w.weightedScore.toFixed(2) : '');

const isDemo = process.env.REACT_APP_THEME === 'demo';

const styles = {
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed',
  },
  cell: {
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
  },
  yellowCell: {
    // Student Achievement #F0CA4D
    backgroundColor: '#F0CA4D',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    fontSize: '1.1rem',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  greenCell: {
    // School Progress Part A (Academic Growth) #92C83E
    backgroundColor: '#92C83E',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    fontSize: '1.1rem',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  redCell: {
    // School Progress Part B (Relative Performance) #F7786B
    backgroundColor: '#F7786B',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    fontSize: '1.1rem',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  purpleCell: {
    // Closing the Gaps #7834A4
    backgroundColor: '#7834A4',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    color: 'white',
    fontSize: '1.1rem',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  grayCell: {
    backgroundColor: 'gray',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  centerCell: {
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    fontSize: '1.1rem',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  headerCell: {
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    backgroundColor: 'white',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: isDemo ? '#E6FBFE' : 'white',
  },
  // Column widths controlled by <colgroup> in renderColGroup().
  colDomain: { width: '33%' },
  colScaled: { width: '8%' },
  colBestOf: { width: '10%' },
  colWt7030: { width: '6%' },
  colEnroll: { width: '9%' },
  colWeight: { width: '9%' },
  colWtdScore: { width: '10%' },
  colOverall: { width: '8%' },
  colRating: { width: '7%' },
  headerCellSchoolName: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  // Hides bottom border for cells in the middle of a visual merge
  noBorderBottom: {
    borderBottom: 'none !important',
  },
  // TEA Rating colors
  ratingA: {
    backgroundColor: '#3a7d34',
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    fontSize: '1.1rem',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  ratingB: {
    backgroundColor: '#345999',
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    fontSize: '1.1rem',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  ratingC: {
    backgroundColor: '#7834A4',
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    fontSize: '1.1rem',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  ratingD: {
    backgroundColor: '#e07838',
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    fontSize: '1.1rem',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
  ratingF: {
    backgroundColor: '#c0392b',
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: isDemo ? '1px solid #ECECEC' : '1px solid black',
    borderBottom: isDemo ? '1px solid #ECECEC' : '1px solid black',
    fontSize: '1.1rem',
    padding: isDemo ? '3px 8px' : '6px 8px',
  },
};

class SummaryDetails extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    report: PropTypes.object.isRequired,
    onChangeReportSection: PropTypes.func,
    isFreeAccount: PropTypes.bool,
    districtOrCampusName: PropTypes.string.isRequired,
    districtOrCampusNumber: PropTypes.string.isRequired,
    hideHeader: PropTypes.bool.isRequired,
    schoolYear: PropTypes.number.isRequired,
    showTenths: PropTypes.bool,
    campusContribution: PropTypes.object,
    totalEnrollment: PropTypes.number,
    wrapInTable: PropTypes.bool,
    campusRows: PropTypes.array,
    profile: PropTypes.object,
  };

  static defaultProps = {
    wrapInTable: true,
  };

  getMaxOfThree(a, b, c) {
    const numbers = [a, b, c].filter((num) => !isNaN(num));
    if (numbers.length === 0) return NaN;
    return Math.max(...numbers);
  }

  getMaxOfThreeColor(d1Score, d2aScaled, d2bScaled, classes) {
    const numbers = [d1Score, d2aScaled, d2bScaled].filter((num) => !isNaN(num));
    if (numbers.length === 0) return NaN;
    var max = Math.max(...numbers);
    if (max == d1Score) {
      return classes.yellowCell;
    } else if (max == d2aScaled) {
      return classes.greenCell;
    } else if (max == d2bScaled) {
      return classes.redCell;
    }
  }

  getRatingClass(letter, classes) {
    switch (letter) {
      case 'A':
        return classes.ratingA;
      case 'B':
        return classes.ratingB;
      case 'C':
        return classes.ratingC;
      case 'D':
        return classes.ratingD;
      case 'F':
        return classes.ratingF;
      default:
        return classes.centerCell;
    }
  }

  getContribValue(domainKey) {
    const { campusContribution } = this.props;
    if (!campusContribution || !campusContribution.domainWeights) return null;
    return campusContribution.domainWeights[domainKey] || null;
  }

  renderColGroup() {
    const { report, campusContribution } = this.props;
    const showContribCols = report.isDistrict || !!campusContribution;
    if (showContribCols) {
      return (
        <colgroup>
          <col style={{ width: '33%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '6%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '7%' }} />
        </colgroup>
      );
    }
    return (
      <colgroup>
        <col style={{ width: '40%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '12%' }} />
      </colgroup>
    );
  }

  renderHeader() {
    const { classes, schoolYear, report, campusContribution } = this.props;
    const showContribCols = report.isDistrict || !!campusContribution;
    return (
      <TableRow className={classes.stickyHeader}>
        <TableCell className={`${classes.headerCell} ${classes.colDomain}`}>Domain</TableCell>
        <TableCell className={`${classes.headerCell} ${classes.colScaled}`}>Scaled Score</TableCell>
        <TableCell className={`${classes.headerCell} ${classes.colBestOf}`}>
          Better of Student Achievement or School Progress
        </TableCell>
        <TableCell className={`${classes.headerCell} ${classes.colWt7030}`}>Domain Weight</TableCell>
        {showContribCols && (
          <React.Fragment>
            <TableCell className={`${classes.headerCell} ${classes.colEnroll}`}>Enrollment (3-12)</TableCell>
            <TableCell className={`${classes.headerCell} ${classes.colWeight}`}>Enrollment Weight</TableCell>
            <TableCell className={`${classes.headerCell} ${classes.colWtdScore}`}>Weighted Score</TableCell>
          </React.Fragment>
        )}
        <TableCell className={`${classes.headerCell} ${classes.colOverall}`}>Overall Score</TableCell>
        <TableCell className={`${classes.headerCell} ${classes.colRating}`}>
          {schoolYear ? schoolYear : ''} Rating
        </TableCell>
      </TableRow>
    );
  }

  // Renders 4 data rows for a given report (district or single campus).
  // No rowSpan — every row has all 9 (or 6) cells. Merged areas use
  // hidden bottom borders and empty cells to simulate the merged look.
  renderDataRows({
    report: rpt,
    classes,
    showContribCols,
    enrollment,
    ratingLetter,
    ratingClass,
    bestOfColor,
    bestOfValue,
    overallScoreDisplay,
    d1w,
    d2aw,
    d2bw,
    d3w,
  }) {
    const nb = classes.noBorderBottom;
    const cc = classes.centerCell;

    return (
      <React.Fragment>
        {/* Row 1: Student Achievement (D1) — merged cells empty, values in row 2 */}
        <TableRow>
          <TableCell className={classes.yellowCell}>Student Achievement</TableCell>
          <TableCell className={`${classes.yellowCell} ${cc}`}>
            {rpt && rpt.d1 && rpt.d1.overall ? rpt.d1.overall.score : ''}
          </TableCell>
          <TableCell className={`${cc} ${bestOfColor} ${nb}`}></TableCell>
          <TableCell className={`${cc} ${nb}`}></TableCell>
          {showContribCols && (
            <React.Fragment>
              <TableCell className={`${cc} ${nb}`}></TableCell>
              <TableCell className={cc}>{weightPct(d1w)}</TableCell>
              <TableCell className={cc}>{weightScore(d1w)}</TableCell>
            </React.Fragment>
          )}
          <TableCell className={`${cc} ${nb}`}></TableCell>
          <TableCell className={`${ratingClass} ${nb}`}></TableCell>
        </TableRow>
        {/* Row 2: School Progress Part A (D2a) — merged cells show values here (visual center) */}
        <TableRow>
          <TableCell className={classes.greenCell}>School Progress, Part A (Academic Growth)</TableCell>
          <TableCell className={`${classes.greenCell} ${cc}`}>{rpt && rpt.d2a ? rpt.d2a.scaled : ''}</TableCell>
          <TableCell className={`${cc} ${bestOfColor} ${nb}`}>{bestOfValue}</TableCell>
          <TableCell className={`${cc} ${nb}`}>{'70%'}</TableCell>
          {showContribCols && (
            <React.Fragment>
              <TableCell className={`${cc} ${nb}`}>{enrollment > 0 ? enrollment : ''}</TableCell>
              <TableCell className={cc}>{weightPct(d2aw)}</TableCell>
              <TableCell className={cc}>{weightScore(d2aw)}</TableCell>
            </React.Fragment>
          )}
          <TableCell className={`${cc} ${nb}`}>{overallScoreDisplay}</TableCell>
          <TableCell className={`${ratingClass} ${nb}`}>{ratingLetter}</TableCell>
        </TableRow>
        {/* Row 3: School Progress Part B (D2b) */}
        <TableRow>
          <TableCell className={classes.redCell}>School Progress, Part B (Relative Performance)</TableCell>
          <TableCell className={`${classes.redCell} ${cc}`}>{rpt && rpt.d2b ? rpt.d2b.scaled : ''}</TableCell>
          <TableCell className={bestOfColor}></TableCell>
          <TableCell className={cc}></TableCell>
          {showContribCols && (
            <React.Fragment>
              <TableCell className={`${cc} ${nb}`}></TableCell>
              <TableCell className={cc}>{weightPct(d2bw)}</TableCell>
              <TableCell className={cc}>{weightScore(d2bw)}</TableCell>
            </React.Fragment>
          )}
          <TableCell className={`${cc} ${nb}`}></TableCell>
          <TableCell className={`${ratingClass} ${nb}`}></TableCell>
        </TableRow>
        {/* Row 4: Closing the Gaps (D3) */}
        <TableRow>
          <TableCell className={classes.purpleCell}>Closing the Gaps</TableCell>
          <TableCell className={`${classes.purpleCell} ${cc}`}>{rpt && rpt.d3 ? rpt.d3.scaled : ''}</TableCell>
          <TableCell className={classes.grayCell}> </TableCell>
          <TableCell className={cc}>{'30%'}</TableCell>
          {showContribCols && (
            <React.Fragment>
              <TableCell className={cc}></TableCell>
              <TableCell className={cc}>{weightPct(d3w)}</TableCell>
              <TableCell className={cc}>{weightScore(d3w)}</TableCell>
            </React.Fragment>
          )}
          <TableCell className={cc}></TableCell>
          <TableCell className={ratingClass}></TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  renderRows() {
    const {
      report,
      classes,
      districtOrCampusName,
      districtOrCampusNumber,
      campusContribution,
      totalEnrollment,
    } = this.props;

    const showContribCols = report.isDistrict || !!campusContribution;
    const colCount = showContribCols ? 9 : 6;
    const enrollment = campusContribution ? campusContribution.campus3To12StudentsCount || 0 : totalEnrollment || 0;
    const ratingLetter = report && report.summary ? report.summary.letter : '';
    const ratingClass = this.getRatingClass(ratingLetter, classes);

    const bestOfColor = this.getMaxOfThreeColor(report.d1.overall.score, report.d2a.scaled, report.d2b.scaled, classes);
    const bestOfValue = this.getMaxOfThree(report.d1.overall.score, report.d2a.scaled, report.d2b.scaled);

    const overallScoreDisplay =
      report && report.summary && !isNaN(report.summary.score)
        ? report.isDistrict
          ? Number(report.summary.score).toFixed(2)
          : Number(report.summary.score).toFixed(0)
        : '';

    return (
      <React.Fragment>
        <TableRow>
          <TableCell colSpan={colCount} className={`${classes.headerCell} ${classes.headerCellSchoolName}`}>
            {districtOrCampusName} (
            {String(districtOrCampusNumber).startsWith('group:') ? 'Campus Group' : districtOrCampusNumber})
          </TableCell>
        </TableRow>
        {this.renderDataRows({
          report: report,
          classes,
          showContribCols,
          enrollment,
          ratingLetter,
          ratingClass,
          bestOfColor,
          bestOfValue,
          overallScoreDisplay,
          d1w: this.getContribValue('Domain1'),
          d2aw: this.getContribValue('Domain2a'),
          d2bw: this.getContribValue('Domain2b'),
          d3w: this.getContribValue('Domain3'),
        })}
      </React.Fragment>
    );
  }

  renderCampusRows(campusInfo) {
    const { classes } = this.props;
    const campusReport = campusInfo.campusResult && campusInfo.campusResult.report;
    if (!campusReport || !campusReport.d1) return null;

    const enrollment = campusInfo.campus3To12StudentsCount || 0;
    const ratingLetter = campusReport && campusReport.summary ? campusReport.summary.letter : '';
    const ratingClass = this.getRatingClass(ratingLetter, classes);

    const bestOfColor = this.getMaxOfThreeColor(
      campusReport.d1.overall.score,
      campusReport.d2a.scaled,
      campusReport.d2b.scaled,
      classes
    );
    const bestOfValue = this.getMaxOfThree(
      campusReport.d1.overall.score,
      campusReport.d2a.scaled,
      campusReport.d2b.scaled
    );

    const overallScoreDisplay =
      campusReport.summary && !isNaN(campusReport.summary.score) ? Number(campusReport.summary.score).toFixed(0) : '';

    const weights = campusInfo.domainWeights || {};

    return (
      <React.Fragment key={campusInfo.campusNumber}>
        <TableRow>
          <TableCell colSpan={9} className={`${classes.headerCell} ${classes.headerCellSchoolName}`}>
            {campusInfo.campusName} ({campusInfo.campusNumber})
          </TableCell>
        </TableRow>
        {this.renderDataRows({
          report: campusReport,
          classes,
          showContribCols: true,
          enrollment,
          ratingLetter,
          ratingClass,
          bestOfColor,
          bestOfValue,
          overallScoreDisplay,
          d1w: weights.Domain1 || null,
          d2aw: weights.Domain2a || null,
          d2bw: weights.Domain2b || null,
          d3w: weights.Domain3 || null,
        })}
      </React.Fragment>
    );
  }

  render() {
    const { classes, wrapInTable, campusRows } = this.props;

    if (!wrapInTable) {
      return this.renderRows();
    }

    return (
      <div id="reportSummaryDetails" className={classes.root}>
        <TableContainer component={Paper}>
          <Table
            aria-label="score table"
            style={{
              tableLayout: 'fixed',
              borderCollapse: 'separate',
              borderSpacing: 0,
              borderTop: isDemo ? '1px solid #ECECEC' : '1px solid black',
              borderLeft: isDemo ? '1px solid #ECECEC' : '1px solid black',
            }}
          >
            {this.renderColGroup()}
            <TableHead>{this.renderHeader()}</TableHead>
            <TableBody>
              {this.renderRows()}
              {campusRows && campusRows.map((campusInfo) => this.renderCampusRows(campusInfo))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  }
}

export default withStyles(styles)(SummaryDetails);
